import React, { useCallback, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { useT } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { chunkedUpload } from '@/lib/upload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { UploadCloud, Film, X, Sparkles } from 'lucide-react';
import UploadProcessing from '@/components/UploadProcessing';

const MAX_BYTES = 5 * 1024 * 1024 * 1024;
const SOURCE_TYPES = ['podcast', 'stream', 'vlog', 'interview', 'gaming', 'sport', 'other'];

// Stable framer-motion props (avoid new object identity on every render)
const FADE_OUT = { opacity: 0 };
const FADE_DURATION = { duration: 0.15 };
const SLIDE_IN_INITIAL = { opacity: 0, y: 10 };
const SLIDE_IN_ANIMATE = { opacity: 1, y: 0 };
const FADE_IN = { opacity: 1 };

const Upload = () => {
  const navigate = useNavigate();
  const { t } = useT();
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [sourceType, setSourceType] = useState('podcast');
  const [progress, setProgress] = useState(0);
  const [stageIndex, setStageIndex] = useState(0);
  const [processing, setProcessing] = useState(false);
  const submittingRef = useRef(false);

  const onDrop = useCallback((accepted, rejections) => {
    if (rejections?.length) {
      toast.error(t('up.rejected'));
      return;
    }
    const f = accepted[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error(t('up.toobig'));
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  }, [title, t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'video/mp4': ['.mp4'], 'video/quicktime': ['.mov'], 'video/x-msvideo': ['.avi'] },
    maxFiles: 1,
  });

  const submit = async () => {
    if (!file) { toast.error(t('up.first')); return; }
    if (submittingRef.current) return;  // guard against double-click
    submittingRef.current = true;
    setProcessing(true);
    setProgress(0);
    setStageIndex(0);
    try {
      const { video_id } = await chunkedUpload(
        file,
        { title: title || file.name, source_type: sourceType },
        (pct) => {
          setProgress(pct);
          setStageIndex(0);
        },
      );
      toast.success(t('up.success', { n: '' }) || 'Upload terminé');
      // Defer navigation one tick so AnimatePresence can finish cleanly
      setTimeout(() => navigate(`/videos/${video_id}`), 50);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('up.fail'));
      setProcessing(false);
      submittingRef.current = false;
    }
  };


  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10" data-testid="upload-page">
      <span className="text-xs uppercase tracking-[0.25em] text-[#D4FF00] font-mono">{t('up.tag')}</span>
      <h1 className="font-heading text-4xl lg:text-5xl font-black mt-2 mb-2 tracking-tighter">{t('up.title')}</h1>
      <p className="text-zinc-400 mb-10">{t('up.subtitle')}</p>

      <AnimatePresence mode="wait" initial={false}>
        {!processing ? (
          <motion.div key="form" exit={FADE_OUT} transition={FADE_DURATION}>
            <div className="w-full space-y-6">
                <div {...getRootProps()} className={`relative rounded-3xl border-2 border-dashed transition cursor-pointer p-12 lg:p-20 text-center ${isDragActive ? 'border-[#D4FF00] bg-[#D4FF00]/5' : 'border-white/10 hover:border-white/25 bg-white/[0.02]'}`} data-testid="upload-dropzone">
                  <input {...getInputProps()} data-testid="upload-file-input" />
                  {file ? (
                    <div className="flex items-center justify-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-[#D4FF00]/10 border border-[#D4FF00]/30 flex items-center justify-center">
                        <Film className="w-6 h-6 text-[#D4FF00]" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold" data-testid="upload-file-name">{file.name}</p>
                        <p className="text-sm text-zinc-500 font-mono">{(file.size / (1024 * 1024)).toFixed(1)} MB · {file.type || 'video'}</p>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setFile(null); }} data-testid="upload-clear-file"><X className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                    <>
                      <motion.div animate={isDragActive ? { scale: 1.1 } : { scale: 1 }} className="w-16 h-16 rounded-2xl bg-[#D4FF00]/10 border border-[#D4FF00]/30 flex items-center justify-center mx-auto mb-6">
                        <UploadCloud className="w-8 h-8 text-[#D4FF00]" />
                      </motion.div>
                      <h3 className="font-heading text-2xl font-bold mb-2">{isDragActive ? t('up.drop.active') : t('up.drop.idle')}</h3>
                      <p className="text-zinc-500 text-sm">{t('up.drop.hint')}</p>
                    </>
                  )}
                </div>

                {file && (
                  <motion.div initial={SLIDE_IN_INITIAL} animate={SLIDE_IN_ANIMATE} className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1.5 block">{t('up.label.title')}</Label>
                      <Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-white/5 border-white/10 h-11" placeholder={t('up.label.title.ph')} data-testid="upload-title-input" />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1.5 block">{t('up.label.type')}</Label>
                      <Select value={sourceType} onValueChange={setSourceType}>
                        <SelectTrigger className="bg-white/5 border-white/10 h-11" data-testid="upload-source-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {SOURCE_TYPES.map((s) => (
                            <SelectItem key={s} value={s} data-testid={`upload-source-${s}`}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </motion.div>
                )}

                <div className="flex justify-end">
                  <Button onClick={submit} disabled={!file || processing} className="bg-[#D4FF00] text-black hover:bg-[#B3D600] font-semibold h-12 px-6 disabled:opacity-30" data-testid="upload-submit-btn">
                    <Sparkles className="w-4 h-4 mr-2" /> {t('up.cta')}
                  </Button>
                </div>
              </div>
          </motion.div>
        ) : (
          <motion.div key="processing" initial={FADE_OUT} animate={FADE_IN}>
            <UploadProcessing progress={progress} stageIndex={stageIndex} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Upload;
